// @flow

import _ from "lodash"
import { List } from "immutable"
import Cookies from "js-cookie"
import { Base64 } from "js-base64"

import Passenger from "../../../../app/scripts/containers/mutable/Passenger"

const PERSIST_STORE_KEYS = {
  RESERVATION_FORM: "RESERVATION_FORM",
  PASSENGERS: "PASSENGERS",
  ANCILLARIES: "ANCILLARIES",
}

// Keep cookies minimal to prevent exceeded cookies size limit
const COOKIES_MAX_LENGTH = 300

export const BASIC_FIELDS: Array<string> = ["title", "firstname", "lastname", "nationality"]
export const BIRTH_FIELDS: Array<string> = ["birthDay", "birthMonth", "birthYear"]
export const ID_FIELDS: Array<string> = [
  "idNumber",
  "idNoExpiration",
  "idExpirationDay",
  "idExpirationMonth",
  "idExpirationYear",
]

export type StoredPassenger = {
  firstname: string,
  middlename: string,
  lastname: string,
  title: string,
  birthDay: string,
  birthMonth: string,
  birthYear: string,
  nationality: string,
  idNumber?: string,
  idExpirationYear?: string,
  idExpirationMonth?: string,
  idExpirationDay?: string,
  idNoExpiration?: string,
  insurance?: string,
  bags?: string,
  paidHandBags?: string,
}

export type StoredAncillaries = {
  additionalServices: Array<string>,
  chosenServicePackage: string,
  bookingToken: string,
}

export type AutofillData = List<StoredPassenger>

export const MIDDLENAME_FIELD: Array<string> = ["middlename"]
// $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
export const ALL_FIELDS = _.union(BASIC_FIELDS, BIRTH_FIELDS, ID_FIELDS)

// eslint-disable-next-line flowtype/no-weak-types
type LOCAL_STORAGE_FORM = any
type FORM_TO_SAVE = any

function saveObfuscatedToCookies(key: string, data: string | string[]): void {
  // eslint-disable-line flowtype/no-weak-types
  try {
    const cookieValue = Base64.encode(JSON.stringify(data)) // obfuscate
    if (cookieValue.length < COOKIES_MAX_LENGTH) {
      Cookies.set(key, cookieValue, { expires: 30 }) // 30 days expiration
    }
  } catch (e) {
    // ignore
  }
}

function saveToSessionStorage(key: string, data: FORM_TO_SAVE): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(data))
  } catch (e) {
    // ignore
  }
}

export function getPassengers(): List<StoredPassenger> {
  const cookiesPassenger = Cookies.get(PERSIST_STORE_KEYS.PASSENGERS)
  try {
    return new List(JSON.parse(Base64.decode(cookiesPassenger || "")))
  } catch (e) {
    return new List([])
  }
}

export function clearSpecificPassenger(passengersData: List<StoredPassenger>, index: number): void {
  saveObfuscatedToCookies(PERSIST_STORE_KEYS.PASSENGERS, passengersData.remove(index))
}

export function getFilteredPassengers(
  filledPassengers: Array<StoredPassenger>,
): List<StoredPassenger> {
  return getPassengers().filter(
    stored => !filledPassengers.find(filled => Passenger.equals(stored, filled)),
  )
}
export function getFilteredMatchingPassengers(
  filled: Array<StoredPassenger>,
  value: string,
): List<StoredPassenger> {
  return getFilteredPassengers(filled).filter(
    passenger =>
      _.isNil(value) ||
      _.startsWith(passenger.firstname && passenger.firstname.toLowerCase(), value.toLowerCase()),
  )
}

export function hasStoredUnfilledPassengers(passengers: Array<StoredPassenger>): boolean {
  return Boolean(getFilteredPassengers(passengers).size)
}

export function getNeededFields(middlenameNeeded: boolean, idNeeded: boolean): Array<string> {
  return [
    ...BASIC_FIELDS,
    ...BIRTH_FIELDS,
    ...(middlenameNeeded ? MIDDLENAME_FIELD : []),
    ...(idNeeded ? ID_FIELDS : []),
  ]
}

// Reservation form
// ---
export function saveReservationForm(data: FORM_TO_SAVE) {
  saveToSessionStorage(PERSIST_STORE_KEYS.RESERVATION_FORM, {
    ...data,
    timestamp: new Date().toISOString(),
  })
}

export function loadReservationForm(): LOCAL_STORAGE_FORM {
  if (!navigator.cookieEnabled) return null
  return JSON.parse(window.sessionStorage.getItem(PERSIST_STORE_KEYS.RESERVATION_FORM))
}

export function clearReservationForm(): void {
  if (!navigator.cookieEnabled) return
  window.sessionStorage.removeItem(PERSIST_STORE_KEYS.RESERVATION_FORM)
}

export function shouldRestoreUnfinishedReservation(): boolean {
  const reservationForm = loadReservationForm()
  const firstPassenger = _.get(reservationForm, ["passengers", 0])
  return _.get(firstPassenger, "firstname") || _.get(firstPassenger, "lastname")
}

// Passengers
// ---
export function savePassengers(unstoredPassengers: Array<StoredPassenger>): void {
  // eslint-disable-line flowtype/no-weak-types
  let storedPassengers = getPassengers()
  const paxes = _.values(unstoredPassengers).map(passenger =>
    _.pick(passenger, getNeededFields(Boolean(passenger.middlename), Boolean(passenger.idNumber))),
  )

  // Control for duplicity as we have own equals
  paxes.forEach((newPassenger, index) => {
    const duplicity = storedPassengers.find(oldPassenger =>
      Passenger.equals(newPassenger, oldPassenger),
    )

    // Handle duplicity
    if (duplicity) {
      storedPassengers = storedPassengers.remove(index)
    }

    storedPassengers = storedPassengers.push(newPassenger)
  })

  saveObfuscatedToCookies(PERSIST_STORE_KEYS.PASSENGERS, storedPassengers.toJS())
}

// Ancillaries
// ---
export const saveAncillaries = (ancillaries: StoredAncillaries): void => {
  saveToSessionStorage(PERSIST_STORE_KEYS.ANCILLARIES, ancillaries)
}

export const loadAncillaries = (bookingToken: string): ?StoredAncillaries => {
  if (!navigator.cookieEnabled || !bookingToken) return null

  try {
    const ancillaries = JSON.parse(window.sessionStorage.getItem(PERSIST_STORE_KEYS.ANCILLARIES))
    return ancillaries.bookingToken === bookingToken ? ancillaries : null
  } catch (e) {
    return null
  }
}

export const clearAncillaries = (): void => {
  if (!navigator.cookieEnabled) return
  window.sessionStorage.removeItem(PERSIST_STORE_KEYS.ANCILLARIES)
}
